import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import products, { getProduct } from "../data/products";
import SectionHeader from "../components/SectionHeader";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const ProductDetail = () => {
  const { slug } = useParams();
  const product = getProduct(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [slug]);

  if (!product) return <Navigate to="/products" replace />;

  const others = products.filter((p) => p.slug !== product.slug);

  return (
    <>
      <section
        className="product-hero"
        style={{ "--product-accent": product.accent }}
      >
        <div className="product-hero__inner">
          <motion.div
            className="product-hero__text"
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            <p className="eyebrow">
              {product.flagship ? "Flagship product" : "Byteflow product"}
            </p>
            <h1>
              <span className="product-hero__icon" aria-hidden>{product.icon}</span>
              {product.name}
            </h1>
            <p className="product-hero__tagline">{product.tagline}</p>
            <p className="product-hero__blurb">{product.heroSummary}</p>
            <div className="hero__ctas">
              <Link to="/contact" className="btn-primary">Request a Demo</Link>
              <Link to="/pricing" className="btn-secondary">See pricing</Link>
            </div>
          </motion.div>
          <motion.div
            className="product-hero__image"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <img src={product.image} alt={`${product.name} preview`} />
          </motion.div>
        </div>
      </section>

      {product.stats && (
        <section
          className="product-stats"
          style={{ "--product-accent": product.accent }}
        >
          <div className="product-stats__grid">
            {product.stats.map((s) => (
              <div key={s.label} className="product-stat">
                <span className="product-stat__value">{s.value}</span>
                <span className="product-stat__label">{s.label}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="section">
        <motion.div
          className="section__header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <p className="eyebrow">Features</p>
          <h2>Everything {product.shortName} teams need</h2>
        </motion.div>

        <div className="modules-grid">
          {product.features.map((f, i) => (
            <motion.div
              key={f.title}
              className="module-card"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
            >
              <div className="module-card__icon" aria-hidden>{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {product.whyChoose && (
        <section
          className="section section--muted"
          style={{ "--product-accent": product.accent }}
        >
          <SectionHeader
            eyebrow="Why we win"
            title={`Why ${product.audienceWord || "teams"} choose ${product.name}`}
          />
          <div className="why-grid">
            {product.whyChoose.map((w, i) => (
              <motion.div
                key={w.title}
                className="why-card"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
              >
                <div className="why-card__icon" aria-hidden>{w.icon}</div>
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {product.mobileApps && (
        <section
          className="section"
          style={{ "--product-accent": product.accent }}
        >
          <SectionHeader
            eyebrow="In your pocket"
            title="Native mobile apps for every role"
            lead="Beyond the web back-office, dedicated apps give each audience exactly what they need — bundled with every plan, never a paid add-on."
          />
          <div className="apps-grid">
            {product.mobileApps.map((app, i) => (
              <motion.div
                key={app.name}
                className="app-card"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: i * 0.07 }}
              >
                <div className="app-card__head">
                  <span aria-hidden>{app.icon}</span> {app.name}
                </div>
                <ul>
                  {app.points.map((pt) => (
                    <li key={pt}>{pt}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {product.implementationPlan && (
        <section
          className="section section--muted"
          style={{ "--product-accent": product.accent }}
        >
          <SectionHeader
            eyebrow="Implementation plan"
            title={product.planTitle || "From kickoff to go-live"}
            lead={product.planLead}
          />
          <ol className="plan-grid">
            {product.implementationPlan.map((p, i) => (
              <motion.li
                key={p.phase}
                className="plan-card"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
              >
                <span className="plan-card__days">{p.days}</span>
                <h3>{p.phase}</h3>
                <p>{p.desc}</p>
              </motion.li>
            ))}
          </ol>
        </section>
      )}

      {product.beforeAfter && (
        <section
          className="section"
          style={{ "--product-accent": product.accent }}
        >
          <SectionHeader
            eyebrow="Before & after"
            title={`Day-to-day life with ${product.name}`}
          />
          <div className="compare-wrap">
            <table className="compare-table">
              <thead>
                <tr>
                  <th>Day-to-day task</th>
                  <th>The old way</th>
                  <th>With {product.name}</th>
                </tr>
              </thead>
              <tbody>
                {product.beforeAfter.map((row) => (
                  <tr key={row.task}>
                    <td>{row.task}</td>
                    <td>{row.before}</td>
                    <td>{row.after}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="section section--muted">
        <motion.div
          className="section__header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <p className="eyebrow">Who it’s for</p>
          <h2>Built for {product.shortName.toLowerCase()} teams like yours</h2>
        </motion.div>
        <div className="industries-grid">
          {product.industries.map((name) => (
            <div key={name} className="industry-card">
              <span className="industry-card__icon" aria-hidden>•</span>
              <span className="industry-card__name">{name}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="section__header">
          <p className="eyebrow">Other products</p>
          <h2>Explore the rest of the Byteflow suite</h2>
        </div>
        <div className="products-grid products-grid--compact">
          {others.map((p) => (
            <Link
              to={`/products/${p.slug}`}
              key={p.slug}
              className="product-card product-card--compact"
              style={{ "--product-accent": p.accent }}
            >
              <div className="product-card__icon" aria-hidden>{p.icon}</div>
              <h3>{p.name}</h3>
              <p className="product-card__tagline">{p.tagline}</p>
              <span className="product-card__link">Learn more →</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="cta-strip">
        <div className="cta-strip__inner">
          <div>
            <h2>Ready to see {product.shortName} in action?</h2>
            <p>Book a 30-minute walkthrough tailored to your workflow.</p>
          </div>
          <Link to="/contact" className="btn-primary btn-primary--inverse">
            Request a Demo
          </Link>
        </div>
      </section>
    </>
  );
};

export default ProductDetail;
